/**
 * Browser-side calendar helpers.
 *
 * The date maths lives in shared/dates.ts so the Worker computes MD labels the
 * same way; only the Intl formatting belongs here.
 */
export * from '@shared/dates';
import { startOfMonth, type WeekStart } from '@shared/dates';

/** Localised weekday initials, ordered to match the grid. */
export function weekdayLabels(locale: string, weekStart: WeekStart = 1): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  // 2024-01-07 was a Sunday, giving a stable reference week.
  return Array.from({ length: 7 }, (_, i) =>
    fmt.format(new Date(Date.UTC(2024, 0, 7 + ((i + weekStart) % 7)))),
  );
}

/**
 * A weekday short enough to head a register column, in the active locale.
 *
 * Latin names abbreviate by truncation: "Wed" becomes "We", "mer." becomes "me".
 * Arabic does not. Every Arabic weekday opens with the definite article, so the
 * first two letters of الأحد and الاثنين are the same two letters and a
 * month of columns reads ال thirty-one times — which is what shipped, and is
 * useless. Dropping the article first gives أح · اث · ثل · أر · خم · جم · سب,
 * distinct from each other and the way an Arabic sheet writes them by hand.
 */
export function abbreviateWeekday(label: string, locale: string): string {
  // Bidi marks are invisible and still count as characters when slicing.
  const bare = label.replace(/[\u200e\u200f\u061c]/g, '').replace(/\.$/, '');
  const stem = locale.startsWith('ar') ? bare.replace(/^\u0627\u0644/, '') : bare;
  return stem.slice(0, 2);
}

/** One formatter, reused across a month of columns. */
export function shortWeekday(locale: string): (date: Date) => string {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  return (date) => abbreviateWeekday(fmt.format(date), locale);
}

export function monthLabel(year: number, month: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(startOfMonth(year, month));
}
